import React, { useContext } from 'react';

import { Row } from 'antd';

import styled from 'styled-components';

import { isBookingEvent } from '@libs/EventTypes';
import { compareAsISODate } from '@libs/NwMoment';
import { mergeCustomFields, parseCustomFieldValue } from '@libs/NwUtils';

import { ListContext } from '@contexts/ListContext';

import HorizontalDate from '@components/Dates/HorizontalDate';
import HorizontalPeriod from '@components/Dates/HorizontalPeriod';
import EventTypeTag from '@components/EventDetails/EventTypeTag';
import { EventWrapper } from '@components/EventsBoxes/EventBoxesUI';
import NwEllipsis from '@components/Gui/NwEllipsis';
import { TableColBorder } from '@components/Gui/StyledComponent';
import ProjectLine from '@components/ProjectLine/ProjectLine';

import NonBookingEventTransactionsSummary from '../Transactions/NonBookingEventTransactionsSummary';
import ProjectTransactionsSummary from '../Transactions/ProjectTransactionsSummary';
import EventBoxesModels from './EventBoxesModels';
import { EventBoxDateAndTime } from './EventBoxesUtils';
import NonBookingEvent from './NonBookingEvent';

const GroupedBookingEvent = ({ modelID, onSelectEvent, page, project, showTransactions, projectsCustomFields }) => {
    // const handleEventClick = () => {
    //     if (onSelectEvent) {
    //         onSelectEvent(project.SearchEvents.filter((event) => event.SearchData.InResults)[0]);
    //     }
    // };

    const mergedCustomFields = project.CustomFields
        ? mergeCustomFields(
              projectsCustomFields.filter((field) => !field.Disabled),
              project.CustomFields,
              true
          )
        : [];

    const { cachedList } = useContext(ListContext);
    const getProjectCategory = (categoryID) => {
        const category = cachedList.projectCategories.items.find((category) => category.ID === categoryID);
        if (category) {
            return category.Name;
        }
    };

    if (project.NotInProject) {
        return (
            <EventWrapper page={page} $noPointer>
                <Row gutter={8} wrap={false}>
                    <EventBoxDateAndTime
                        isGrouped
                        isMultiDay
                        firstDate={project.FirstDate}
                        lastDate={project.LastDate}
                    />
                    <TableColBorder flex="auto" $isGrouped>
                        {/* onClick={handleEventClick}  */}
                        <div className="project-wrapper no-project">
                            <div className="project-header-wrapper">
                                <div className="project-title-wrapper">
                                    <small>
                                        <strong>NO PROJECT</strong>
                                    </small>
                                </div>
                            </div>
                            <div className="project-events-wrapper">
                                <div
                                    className="event-in-project-box"
                                    onClick={() => {
                                        onSelectEvent(project.SearchEvents[0]);
                                    }}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div className="event-in-project-box-details no-border">
                                        <NonBookingEvent event={project.SearchEvents[0]} isGrouped />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </TableColBorder>
                    {page !== 'project' && showTransactions && (
                        <TableColBorder flex="30rem">
                            <NonBookingEventTransactionsSummary
                                event={project.SearchEvents[0]}
                                modelID={modelID}
                                projectId={project.ID}
                            />
                        </TableColBorder>
                    )}
                </Row>
            </EventWrapper>
        );
    }

    return (
        <EventWrapper page={page} $noPointer>
            <Row gutter={8} wrap={false}>
                <EventBoxDateAndTime isGrouped isMultiDay firstDate={project.FirstDate} lastDate={project.LastDate} />
                <TableColBorder flex="auto" $isGrouped>
                    {/* onClick={handleEventClick} */}
                    <div className="project-wrapper">
                        <div className="project-header-wrapper">
                            <div className="project-header-left">
                                <div className="project-title-wrapper">
                                    <ProjectLine link project={project} />
                                </div>
                                {project.Product && (
                                    <div className="project-product-wrapper">
                                        <p>{project.Product}</p>
                                    </div>
                                )}
                                {project.ProjectCategoryID && (
                                    <div className="project-category-wrapper">
                                        <p>{getProjectCategory(project.ProjectCategoryID)}</p>
                                    </div>
                                )}
                                {project.Description && (
                                    <div className="project-description-wrapper">
                                        <NwEllipsis
                                            style={{ fontStyle: 'italic' }}
                                            noFontSize
                                            text={project.Description}
                                            rows={2}
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="project-header-right">
                                {mergedCustomFields.map((field, index) => (
                                    <p key={`custom-field-${field.Name}`}>
                                        <em>{field.Name.replace(/_/g, ' ')}:</em> {parseCustomFieldValue(field)}
                                    </p>
                                ))}
                            </div>
                        </div>
                        <div className="project-events-wrapper">
                            {project.SearchEvents.map((event) =>
                                event.SearchData.InResults ? (
                                    <div
                                        className="event-in-project-box"
                                        key={event.SearchData.ListKey}
                                        onClick={() => {
                                            onSelectEvent(event);
                                        }}
                                    >
                                        <div className="event-in-project-box-date">
                                            {compareAsISODate(event.SearchData.StartDate, event.SearchData.EndDate) ? (
                                                <HorizontalDate date={event.SearchData.StartDate} size="small" />
                                            ) : (
                                                <HorizontalPeriod
                                                    startDate={event.SearchData.StartDate}
                                                    endDate={event.SearchData.EndDate}
                                                    size="small"
                                                />
                                            )}
                                        </div>
                                        <div
                                            className={`event-in-project-box-details`} // ${event.SearchData.Cancel ? ' event-in-project-cancel' : ''}
                                        >
                                            {isBookingEvent(event.SearchData.Type) ? (
                                                <>
                                                    <div
                                                        className={`event-in-project-box-details-title ${event.SearchData.Cancel ? ' event-in-project-cancel' : ''}`}
                                                    >
                                                        <EventTypeTag event={event} type={event.SearchData.Type} />
                                                        <p>{event.SearchData.Title}</p>
                                                    </div>
                                                    <div
                                                        className={`event-in-project-box-details-description ${event.SearchData.Cancel ? ' event-in-project-cancel' : ''}`}
                                                    >
                                                        <NwEllipsis noFontSize text={event.Description} rows={2} />
                                                    </div>
                                                    <EventBoxesModels page={page} event={event} />
                                                </>
                                            ) : (
                                                <NonBookingEvent event={event} isGrouped />
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div
                                        className="event-in-project-box not-result"
                                        key={event.SearchData.ListKey}
                                        onClick={() => {
                                            onSelectEvent(event);
                                        }}
                                    >
                                        <div className="event-in-project-box-date">
                                            {compareAsISODate(event.SearchData.StartDate, event.SearchData.EndDate) ? (
                                                <HorizontalDate date={event.SearchData.StartDate} size="small" />
                                            ) : (
                                                <HorizontalPeriod
                                                    startDate={event.SearchData.StartDate}
                                                    endDate={event.SearchData.EndDate}
                                                    size="small"
                                                />
                                            )}
                                        </div>
                                        <div
                                            className={`event-in-project-box-details ${event.SearchData.Cancel ? ' event-in-project-cancel' : ''}`}
                                        >
                                            <div className="event-in-project-box-details-title">
                                                <EventTypeTag event={event} type={event.SearchData.Type} />
                                                <p>{event.SearchData.Title}</p>
                                            </div>
                                        </div>
                                    </div>
                                )
                            )}
                        </div>
                    </div>
                </TableColBorder>
                {page !== 'project' && showTransactions && (
                    <TableColBorder flex="30rem">
                        <ProjectTransactionsSummary project={project} modelID={modelID} projectId={project.ID} />
                    </TableColBorder>
                )}
            </Row>
        </EventWrapper>
    );
};

export default GroupedBookingEvent;
